<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormatters } from '@lib/composables/useFormatters'
import type { TicketInfoData } from '@/composables/useTicketVerify'

const props = defineProps<{ data: TicketInfoData }>()
const { t } = useI18n()
const fmt = useFormatters()

const showDebug = ref(false)

const zones = computed(() => (props.data.zones ?? []).filter((z): z is NonNullable<typeof z> => z != null))
const slots = computed(() => (props.data.slots ?? []).filter((s): s is NonNullable<typeof s> => s != null))
const usageForZone = computed(() =>
    (props.data.usageForZone ?? []).filter((u): u is NonNullable<typeof u> => u != null)
)
const usageLogs = computed(() => (props.data.usageLogs ?? []).filter((l): l is NonNullable<typeof l> => l != null))

const prettyData = computed(() => JSON.stringify(props.data, null, 2))
</script>

<template>
    <!-- Not found / invalid -->
    <div v-if="!data.ok" class="bg-red-l text-red rounded-12 px-4 py-3 text-center font-medium">
        ❌ <span v-if="data.message">{{ data.message }}</span><span v-else>{{ t('tickets.verify.notFound') }}</span>
    </div>

    <div v-else class="space-y-3 text-sm">
        <!-- Summary -->
        <table class="w-full bg-white border border-gray-200 rounded-12 overflow-hidden">
            <tbody>
                <tr class="border-b border-gray-100">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.found') }}</td>
                    <td class="p-2">✅</td>
                </tr>
                <tr class="border-b border-gray-100">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.email') }}</td>
                    <td class="p-2 text-ink">{{ data.email }}</td>
                </tr>
                <tr class="border-b border-gray-100">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.entries') }}: {{ data.entries }}</td>
                    <td class="p-2 text-ink">{{ t('tickets.verify.people') }}: {{ data.people }}</td>
                </tr>
                <tr v-if="(data.entries ?? 0) > 1" class="border-b border-gray-100">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.entryPause') }}</td>
                    <td class="p-2 text-ink">{{ data.entryPause }}</td>
                </tr>
                <tr class="border-b border-gray-100">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.purchased') }}</td>
                    <td class="p-2 text-ink">{{ fmt.dateTime(data.created) }}</td>
                </tr>
                <tr v-if="data.validUntil">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.validUntil') }}</td>
                    <td class="p-2 text-ink">{{ fmt.dateTime(data.validUntil) }}</td>
                </tr>
            </tbody>
        </table>

        <!-- Event / ticketset / price -->
        <table class="w-full bg-white border border-gray-200 rounded-12 overflow-hidden">
            <tbody>
                <tr class="border-b border-gray-100">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.event') }}</td>
                    <td class="p-2 text-ink">
                        {{ data.eventName }}
                        <small class="text-gray-400">({{ data.eventId }} / {{ data.eventBzucoId }})</small>
                    </td>
                </tr>
                <tr class="border-b border-gray-100">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.ticketset') }}</td>
                    <td class="p-2 text-ink">
                        {{ data.ticketsetName }}
                        <small class="text-gray-400">({{ data.ticketsetId }} / {{ data.ticketsetBzucoId }})</small>
                    </td>
                </tr>
                <tr class="border-b border-gray-100">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.price') }}</td>
                    <td class="p-2 text-ink">
                        {{ data.priceName }}
                        <small class="text-gray-400">({{ data.priceId }} / {{ data.priceBzucoId }})</small>
                    </td>
                </tr>
                <tr class="border-b border-gray-100">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.zones') }}</td>
                    <td class="p-2 text-ink">
                        <template v-if="zones.length">
                            <div v-for="zone in zones" :key="zone.id ?? zone.name ?? ''">
                                {{ zone.name }} <small class="text-gray-400">({{ zone.id }} / {{ zone.bzucoId }})</small>
                            </div>
                        </template>
                        <template v-else>{{ t('tickets.verify.noZones') }}</template>
                    </td>
                </tr>
                <tr>
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.slots') }}</td>
                    <td class="p-2 text-ink">
                        <template v-if="slots.length">
                            <div v-for="slot in slots" :key="slot.id ?? slot.startAt ?? ''">
                                {{ fmt.dateTime(slot.startAt) }}
                                <small class="text-gray-400">({{ slot.id }} / {{ slot.bzucoId }})</small>
                            </div>
                        </template>
                        <template v-else>{{ t('tickets.verify.noSlots') }}</template>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Usage per zone -->
        <table v-if="usageForZone.length" class="w-full bg-white border border-gray-200 rounded-12 overflow-hidden">
            <tbody>
                <tr v-for="row in usageForZone" :key="row.id ?? row.name ?? ''" class="border-b border-gray-100">
                    <td class="p-2 text-gray-500">{{ t('tickets.verify.usageForZone') }}<br />{{ row.name }}</td>
                    <td class="p-2 text-ink">
                        <template v-if="row.remains === null || row.remains === undefined">
                            <span v-if="!row.used">✅</span><span v-else>❌</span>
                        </template>
                        <template v-else>
                            {{ t('tickets.verify.remains') }}:
                            <span v-if="row.remains">✅ {{ row.remains }}</span>
                            <span v-else>❌ —</span>
                            <!-- display is trusted server-rendered HTML from Ticket.get_usage(); never user input -->
                            <div v-if="row.display" class="text-gray-500 mt-1" v-html="row.display" />
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Usage logs -->
        <table v-if="usageLogs.length" class="w-full bg-white border border-gray-200 rounded-12 overflow-hidden">
            <thead>
                <tr class="border-b border-gray-200">
                    <th colspan="3" class="p-2 text-left font-semibold text-ink">{{ t('tickets.verify.usageLogs') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, i) in usageLogs" :key="i" class="border-b border-gray-100">
                    <td class="p-2 text-ink">{{ fmt.dateTime(row.created) }}</td>
                    <td class="p-2 text-ink">
                        {{ row.deviceName }} <small class="text-gray-400">({{ row.deviceId }})</small>
                    </td>
                    <td class="p-2 text-ink">
                        {{ row.zoneName }} <small class="text-gray-400">({{ row.zoneId }})</small>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <!-- Debug: raw JSON behind a toggle -->
    <div class="pt-2">
        <button
            type="button"
            class="px-2 py-1 text-xs rounded-6 bg-gray-100 hover:bg-gray-200 text-gray-500 font-mono"
            @click="showDebug = !showDebug"
        >
            {{ t('tickets.verify.showDebug') }}
        </button>
        <div v-if="showDebug" class="mt-2">
            <div class="text-xs text-gray-400">{{ t('tickets.verify.debugData') }}</div>
            <pre class="text-[10px] bg-gray-900 text-gray-100 rounded-6 p-2 overflow-x-auto">{{ prettyData }}</pre>
        </div>
    </div>
</template>
